import context from '../../config/context'
import { useEffect, useRef, useState } from 'react'
import PasswordPolicies from '../PasswordPolicies'
import {
  DIFFERENT_PASSWORDS_ERROR_MESSAGE,
  PASSWORD,
  PASSWORD_CONFIRMATION,
} from '../../constants/ui.labels'
import PasswordInputFieldGroup from '../PasswordInputFieldGroup'
import Box from '@talkdesk/cobalt-react-box'

const VALIDATION_DELAY = 500

const hasMatchingPasswords = (password, passwordConfirmation) =>
  password === passwordConfirmation

const hasBothPasswordsPresent = (password, passwordConfirmation) =>
  Boolean(password && passwordConfirmation)

const evaluatePasswordDiff = (password, passwordConfirmation) => {
  const bothPresent = hasBothPasswordsPresent(password, passwordConfirmation)
  const matching = hasMatchingPasswords(password, passwordConfirmation)

  const isValid = bothPresent && matching

  const error =
    !bothPresent || matching ? null : DIFFERENT_PASSWORDS_ERROR_MESSAGE

  return { isValid, error }
}

const PasswordChangeFieldGroup = ({
  formData,
  onFormChange,
  disabledForm = false,
}) => {
  const [focus, setFocus] = useState(false)
  const timeoutRef = useRef(null)
  // Keep a ref to latest formData/onFormChange for use inside the delayed
  // validator callback.
  const formDataRef = useRef(formData)
  const onFormChangeRef = useRef(onFormChange)
  useEffect(() => {
    formDataRef.current = formData
    onFormChangeRef.current = onFormChange
  })

  // Clear any pending timer on unmount.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    },
    [],
  )

  const updateFormData = (password, passwordConfirmation) => {
    const { isValid, error } = evaluatePasswordDiff(
      password,
      passwordConfirmation,
    )

    onFormChange({
      ...formData,
      password,
      passwordConfirmation,
      isValid,
    })

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      onFormChangeRef.current({ ...formDataRef.current, error })
    }, VALIDATION_DELAY)
  }

  const handlePasswordChange = (event) => {
    const password = event.target.value
    updateFormData(password, formData.passwordConfirmation)
  }

  const handlePasswordConfirmationChange = (event) => {
    const passwordConfirmation = event.target.value
    updateFormData(formData.password, passwordConfirmation)
  }

  const handleOnFocus = () => setFocus(true)
  const handleOnBlur = () => setFocus(false)

  const { password, passwordConfirmation } = formData

  return (
    <Box>
      <PasswordInputFieldGroup
        label={PASSWORD}
        disabledForm={disabledForm}
        placeholder={PASSWORD}
        password={password}
        onPasswordChange={handlePasswordChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
      />
      {focus ? (
        <PasswordPolicies passwordPolicies={context.passwordPolicies} />
      ) : null}
      <PasswordInputFieldGroup
        label={PASSWORD_CONFIRMATION}
        disabledForm={disabledForm}
        placeholder={PASSWORD_CONFIRMATION}
        password={passwordConfirmation}
        onPasswordChange={handlePasswordConfirmationChange}
      />
    </Box>
  )
}

PasswordChangeFieldGroup.VALIDATION_DELAY = VALIDATION_DELAY

export default PasswordChangeFieldGroup
